import { action, computed, observable } from 'mobx'
import type {
	Localization,
	OrderTypeFlow,
	LocalizationDependencies,
	Configuration,
	SavedAddress,
	NonSavedAddress,
	SavingAddressData,
	OtherSavedAddress,
	StoreResponse,
} from './types'
import { AddressLabel, LocalizationFlow, FlowStep, OrderType } from './types'

const MAXIMUM_SAVED_ADDRESSES = 10

export default class LocalizationStore implements Localization {
	constructor(dependencies: LocalizationDependencies) {
		this.dependencies = dependencies
	}

	private readonly dependencies: LocalizationDependencies

	config = () =>
		// must be replaced by the content of appParams.features.localization
		this.dependencies.config() || defaultLocalization

	get = () => this.dependencies.get()

	@computed get savedAddressesEnabled(): boolean {
		return !!this.config().savedAddressesEnabled
	}

	@observable actualFlow: LocalizationFlow | null = this.get()

	@observable processingFlow: LocalizationFlow | null = null

	@observable editing = false

	@observable orderTypeFlows: OrderTypeFlow[] = []

	@observable storesOnActualAddress: StoreResponse[] = []

	@action setOrderTypeFlows = (orderTypeFlows: OrderTypeFlow[]) => {
		this.orderTypeFlows = orderTypeFlows
	}

	@observable private _savingAddressData: SavingAddressData | null = null

	@computed get savingAddressData(): SavingAddressData | null {
		return this._savingAddressData
	}

	@computed get savingAddress(): boolean {
		return !!this._savingAddressData
	}

	@action setSavingAddressLabel = (label: AddressLabel) => {
		if (this._savingAddressData) {
			this._savingAddressData.label = label
		}
	}

	@action setSavingAddressCustomLabel = (customLabel: string) => {
		if (this._savingAddressData) {
			this._savingAddressData.customLabel = customLabel
		}
	}

	@action startSavingAddress = () => {
		this._savingAddressData = {
			label: AddressLabel.Home,
		}

		this.started = true
	}

	@action stopSavingAddress = () => {
		this._savingAddressData = null
	}

	@observable savedAddresses: SavedAddress[] = []

	@observable _savedAddressesFetched = false

	@computed get savedAddressesFetched(): boolean {
		return this._savedAddressesFetched
	}

	@action fetchSavedAddresses = async () => {
		this.savedAddresses = await this.dependencies.fetchSavedAddresses()
		this._savedAddressesFetched = true
	}

	@action clearSavedAddresses = () => {
		this.savedAddresses = []
		this._savedAddressesFetched = false
	}

	@action saveCurrentAddress = async () => {
		const address = this.processingFlow?.address

		if (!address || !this._savingAddressData) {
			const err = new Error('Missing data required to save an address')
			this.dependencies.emitAddressFailedToSaveEvent(err)

			throw err
		}

		const savedAddress = await this.saveNewAddress(address, this._savingAddressData.label, this._savingAddressData.customLabel)

		this.processingFlow?.selectSavedAddress(savedAddress)

		return savedAddress
	}

	@action saveNewAddress = async (address: NonSavedAddress, label: AddressLabel, customLabel?: string) => {
		try {
			const savedAddress =
				label === AddressLabel.Other && customLabel
					? await this.dependencies.saveNewAddress(address, label, customLabel)
					: await this.dependencies.saveNewAddress(address, label)

			this.savedAddresses.push(savedAddress)
			this.dependencies.emitAddressSavedEvent(savedAddress)

			return savedAddress
		} catch (err) {
			this.dependencies.emitAddressFailedToSaveEvent(err)
			throw err
		}
	}

	@action editSavedAddress = async (address: SavedAddress) => {
		await this.dependencies.updateSavedAddress(address)

		const index = this.savedAddresses.findIndex((sa) => sa._id === address._id)
		this.savedAddresses[index] = address
	}

	@action removeSavedAddress = async (addressId: string) => {
		await this.dependencies.deleteSavedAddress(addressId)
		this.savedAddresses = [...this.savedAddresses.filter((sa) => sa._id !== addressId)]
	}

	getSavedAddress = (addressId: string) => this.savedAddresses.find((address) => address._id === addressId)

	@computed get reachedMaximumSavedAddresses(): boolean {
		return this.savedAddresses.length >= MAXIMUM_SAVED_ADDRESSES
	}

	@observable started = false

	@observable currentStep: FlowStep | null = null

	@computed get alreadyLocalized(): boolean {
		return (
			this.actualFlow?.address !== null &&
			this.actualFlow?.address !== undefined &&
			this.actualFlow?.store !== null &&
			this.actualFlow?.store !== undefined
		)
	}

	@observable startedFromSavedAddress = false

	@observable manualStoreSelection = false

	/*
	 * Fetches and filters out, and saves relevant stores for the address
	 * finishes localization if a matching store is found and we can continue
	 */
	matchStoreForCurrentAddress = async (continueFlowpWhenNoStoreMatched = true) => {
		if (!this.processingFlow?.orderTypeFlow || !this.processingFlow.address) {
			return
		}

		const { stores, shouldGoToFirstStoreFound } = await this.dependencies.getStoresOnAddress(this.processingFlow.address)
		const { orderType, requireAddress } = this.processingFlow.orderTypeFlow

		const relevantStores = stores.filter(
			({ availability }) => availability?.[orderType]?.availableForAddress && (availability.orderOnline || availability.preOrder)
		)

		if (relevantStores.length === 1 || (relevantStores.length > 0 && shouldGoToFirstStoreFound && !this.manualStoreSelection)) {
			const [firstStore] = relevantStores

			this.processingFlow.setStore({
				id: firstStore.id,
				name: firstStore.title,
				address: firstStore.address.formatted,
			})

			// If order type flow doesn't require user address, we set the flow address to the store address 
			if(!requireAddress) {
				this.processingFlow.setAddress({
					formatted: firstStore.address.formatted,
					location: {
						lat: firstStore.address.latLng.lat,
						lng: firstStore.address.latLng.lng
					},
					components: []
				})
			}

			this.end()
			return
		}

		this.dependencies.updateMapStores(stores)
		this.processingFlow.setOrderTypeFlowDisplayAvailableStores(true)

		if(continueFlowpWhenNoStoreMatched) {
			this.nextStep()
		}
	}

	availableStoresOnActualAddress = async () => {
		if (this.actualFlow?.address) {
			const orderType = this.actualFlow?.orderTypeFlow?.orderType
			const orderTypeRequireAddress = this.actualFlow?.orderTypeFlow?.requireAddress
			const { stores } = await this.dependencies.getStoresOnAddress(this.actualFlow.address)

			if (orderType && stores.length > 0) {
				this.storesOnActualAddress = stores.filter(
					({ availability }) => (!orderTypeRequireAddress || availability?.[orderType]?.availableForAddress) && (availability.orderOnline || availability.preOrder)
				)
				
				return
			}
		}

		this.storesOnActualAddress = []
	}

	@action start = async () => {
		// stop the flow if order type flows are unavailable
		const { orderTypeFlowsUnavailable, skipOrderTypeSelection } = await this.getAndSetOrderTypeFlows()
		if (orderTypeFlowsUnavailable) {
			this.dependencies.emitOrderTypeFlowsUnavailable()
			return
		}

		// Start and instanciate the flow
		this.started = true
		this.processingFlow = new LocalizationFlow()

		// if config skipOrderTypeSelection is true and only one order type flow is available
		// we force the order type selection and move to the next step
		if (skipOrderTypeSelection) {
			this.processingFlow.setOrderTypeFlow(this.orderTypeFlows[0])
			this.nextStep()
			this.sendLocalizationStartEvent()
			return
		}

		this.currentStep = FlowStep.OrderTypeSelection
	}

	@action startFromSavedAddress = async (address?: SavedAddress) => {
		this.startedFromSavedAddress = true
		this.sendLocalizationStartEvent()

		const filteredOrderTypesFlows = await this.getFilteredOrderTypeFlows()
		const deliveryOrderTypeFlow = filteredOrderTypesFlows.find((fotf) => fotf.orderType === OrderType.Delivery)

		// Delivery is available, continuing the flow
		if (deliveryOrderTypeFlow) {
			this.processingFlow = new LocalizationFlow()
			this.processingFlow.setOrderTypeFlow(deliveryOrderTypeFlow)
			if (address) {
				this.processingFlow.selectSavedAddress(address)
			}

			this.goToStep(FlowStep.AddressSelection)
			return {}
		}
		return { deliveryUnavailable: true }
	}

	sendLocalizationStartEvent = () => {
		this.dependencies.sendLocalizationStartEvent()
	}

	shouldSkipOrderTypeSelection = () => {
		return this.config().canSkipOrderTypeSelection && this.orderTypeFlows.length === 1
	}

	getAndSetOrderTypeFlows = async() => {
		const filteredOrderTypeFlows = await this.getFilteredOrderTypeFlows()
		this.setOrderTypeFlows(filteredOrderTypeFlows)

		return { 
			orderTypeFlowsUnavailable: this.orderTypeFlows.length === 0,
			skipOrderTypeSelection: this.shouldSkipOrderTypeSelection()
		}

		// orderTypeFlowsUnavailable: indicate that all the order type flows are unavailable, it happens when
		// 	- all stores are closed
		// 	- defined order type flows don't match with the available stores

		// skipOrderTypeSelection: indicate that order type selection must be skipped, it happens when
		// 	- localization configuration has canSkipOrderTypeSelection set to true
		// 	- only one order type flow is available
	}

	getFilteredOrderTypeFlows = async () => {
		const stores = await this.dependencies.getStores()
		const deliveryEnabled = stores.deliveryStores === true || false
		const pickupEnabled = stores.disablePickup === false || false

		// Filter the order type flows
		return this.config().orderTypeFlows.filter((dotf) => {
			// if deliveryEnabled : requireAddress has to be true
			if (dotf.orderType === OrderType.Delivery && deliveryEnabled && dotf.requireAddress) {
				return true
			}

			// if pickEnabled : requireAddress has to be false
			if (dotf.orderType === OrderType.Pickup && pickupEnabled && !dotf.requireAddress) {
				return true
			}

			return false
		})
	}

	@action abort = () => {
		this.started = false
		this.startedFromSavedAddress = false
		this.processingFlow = null
		this.editing = false
		this.currentStep = null
		this.manualStoreSelection = false
		this.dependencies.clearCouponToApply()
		this.stopSavingAddress()
	}

	@action end = async () => {
		this.started = false
		this.startedFromSavedAddress = false
		this.currentStep = null
		this.manualStoreSelection = false

		if (!this.processingFlow?.store || !this.processingFlow.address) {
			this.processingFlow = null
			return
		}

		const response = await this.dependencies.initSessionAndLoadMenu(this.processingFlow.store.id)

		// Editing saved address
		if (response === true && this.processingFlow.savedAddressId && this._savingAddressData) {
			try {
				await this.editSavedAddress({
					...this.processingFlow.address,
					_id: this.processingFlow.savedAddressId,
					label: this._savingAddressData.label,
					customLabel: (this._savingAddressData as OtherSavedAddress).customLabel,
				})
			} catch (_err) {}
		}

		// Adding a new saved address
		else if (response === true && this.savingAddress) {
			try {
				const savedAddress = await this.saveCurrentAddress()
				this.processingFlow.selectSavedAddress(savedAddress)
			} catch (_err) {}
		}

		// Initiate actual flow & redirect to localized menu
		if (response === true) {
			const flow = new LocalizationFlow()
			flow.setOrderTypeFlow(this.processingFlow.orderTypeFlow!)
			flow.setStore(this.processingFlow.store)
			flow.setSavedAddressId(this.processingFlow.savedAddressId)
			flow.setAddress(this.processingFlow.address)

			this.actualFlow = flow
			this.dependencies.save(flow!)

			await this.dependencies.goToLocalizedMenu()
		}

		// If processing flow store is not available and an actual flow store is already set (ex, durimg editing),
		// we re-init the session of the previous store
		else if (this.actualFlow?.store?.id) {
			await this.dependencies.initSession(this.actualFlow.store.id)
		}

		if (this.actualFlow?.store && this.actualFlow.orderTypeFlow) {
			this.dependencies.sendLocalizationFinishEvent(this.actualFlow.store.id, this.actualFlow.orderTypeFlow?.orderType)
		}

		this.processingFlow = null
		this.stopSavingAddress()
	}

	@action edit = async() => {
		if (!this.actualFlow) {
			console.error('error edit: actualFlow is null')
			return
		}

		// stop the flow if order type flows are unavailable
		const { orderTypeFlowsUnavailable } = await this.getAndSetOrderTypeFlows()
		if(orderTypeFlowsUnavailable) {
			this.dependencies.emitOrderTypeFlowsUnavailable()
			return
		}

		this.started = true
		this.editing = true
		this.currentStep = null

		this.processingFlow = new LocalizationFlow()
		this.processingFlow.setOrderTypeFlow(this.actualFlow.orderTypeFlow!)
		this.processingFlow.setStore(this.actualFlow.store!)
		this.processingFlow.setAddress(this.actualFlow.address!)
		this.processingFlow.setSavedAddressId(this.actualFlow.savedAddressId)
		this.processingFlow.setOrderTypeFlowDisplayAvailableStores(true)
	}

	@action editAction = (flowStep: FlowStep) => {
		this.sendLocalizationStartEvent()
		this.manualStoreSelection = true
		this.goToStep(flowStep)
	}

	@action canGoToStep = (step: FlowStep): boolean => {
		if (this.processingFlow?.orderTypeFlow === null || this.processingFlow?.orderTypeFlow === undefined) {
			return false
		}

		return this.processingFlow.orderTypeFlow.steps.some((s) => s === step)
	}

	@action goToStep = (step: FlowStep) => {
		if (this.processingFlow?.orderTypeFlow === null || this.processingFlow?.orderTypeFlow === undefined) {
			return
		}

		const stepIndex = this.processingFlow.orderTypeFlow.steps.findIndex((s) => s === step)
		this.currentStep = this.processingFlow.orderTypeFlow.steps[stepIndex]
	}

	@action nextStep = () => {
		if (!this.processingFlow?.orderTypeFlow) {
			return
		}

		// if we are at the start of the localization flow (order type view), move to the first step
		if (this.currentStep === FlowStep.OrderTypeSelection) {
			this.currentStep = this.processingFlow.orderTypeFlow.steps[0]
			return
		}

		const modalCurrentStepIndex = this.processingFlow.orderTypeFlow.steps.findIndex((s) => s === this.currentStep)

		// if current step is the last step
		if (modalCurrentStepIndex === this.processingFlow.orderTypeFlow.steps.length - 1) {
			this.end()
			return
		}

		// move to the next step
		this.currentStep = this.processingFlow.orderTypeFlow.steps[modalCurrentStepIndex + 1]
	}

	@action previousStep = async () => {
		if (this.processingFlow?.orderTypeFlow === null || this.processingFlow?.orderTypeFlow === undefined) {
			return
		}

		const modalCurrentStepIndex = this.processingFlow.orderTypeFlow.steps.findIndex((s) => s === this.currentStep)

		// if current step is the first step, return to the start of the localization flow (order type view)
		if (modalCurrentStepIndex === 0) {
			// execpt if flow started from saved address, OR skipOrderTypeSelection is true
			const { skipOrderTypeSelection } = await this.getAndSetOrderTypeFlows()
			if (this.startedFromSavedAddress || skipOrderTypeSelection) {
				// and editing is active, we set to null the currentStep
				if (this.editing) {
					this.currentStep = null
				}

				// else, we abort the flow
				else {
					this.abort()
				}
			}

			// Else we set OrderTypeSelection as currentStep
			else {
				this.currentStep = FlowStep.OrderTypeSelection
			}

			return
		}

		// move to the previous step
		this.currentStep = this.processingFlow.orderTypeFlow.steps[modalCurrentStepIndex - 1]
	}

	@action goToLocalizedMenu = async () => {
		// init a new session before redirecting, in case of an order-lock is active
		const result = await this.dependencies.initSession(this.actualFlow?.store?.id)
		if (result === '') {
			return
		}

		// handle the menu redirection here...
		await this.dependencies.goToLocalizedMenu()
	}

	@action getLocalizedMenuPath = () => this.dependencies.getLocalizedMenuPath()

	@action getValidateAddress = () => {
		if (this.actualFlow?.address) {
			return this.dependencies.getValidateAddress(this.actualFlow.address)
		}
	}

	@action forceLocalizationToStore = async (storeId: string) => {
		const store = await this.dependencies.getStoreDetailsById(storeId)
		this.dependencies.checkPickupAvailabilityByStore(store)

		const filteredOrderTypeFlows = await this.getFilteredOrderTypeFlows()
		const pickupOrderTypeFlow = filteredOrderTypeFlows.find((otf) => otf.orderType === OrderType.Pickup)

		// If pickup exists in the available order type flow
		if (pickupOrderTypeFlow) {
			const flow = new LocalizationFlow()
			flow.setOrderTypeFlow(pickupOrderTypeFlow)
			flow.setAddress({
				formatted: store.location.address,
				location: {
					lat: store.location.coordinates.lat,
					lng: store.location.coordinates.lng,
				},
				components: [],
			})
			flow.setStore({
				id: store.id,
				name: store.title,
				address: store.location.address,
			})

			this.actualFlow = flow

			// handle the menu redirection here...
			if (this.actualFlow?.store) {
				await this.dependencies.initSessionAndLoadMenu(this.actualFlow.store.id)
				await this.dependencies.goToLocalizedMenu()
			}

			this.dependencies.save(this.actualFlow!)
		}
	}
}

const defaultOrderTypeFlows: OrderTypeFlow[] = [
	{
		orderType: OrderType.Delivery,
		orderTypeImageSrc: '',
		orderTypeTranslationKey: 'delivery',
		steps: [FlowStep.AddressSelection, FlowStep.AddressDetailsSelection, FlowStep.StoreOnMapSelection],
		requireAddress: true,
	},
	{
		orderType: OrderType.Pickup,
		orderTypeImageSrc: '',
		orderTypeTranslationKey: 'peakup',
		steps: [FlowStep.StoreOnMapSelection],
		requireAddress: false,
	},
]

const defaultLocalization: Configuration = {
	orderTypeFlows: defaultOrderTypeFlows,
	canSkipOrderTypeSelection: true,
	displayToMenuButton: true,
	enabled: true,
}
